import { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '../stores/authStore';
import type { EchoReaderStory } from '../types';

export interface GenerateStoryRequest {
  title?: string;
  theme?: string;
  difficulty_level: 'easy' | 'medium' | 'hard';
  age_group: '4-6' | '7-9' | '10-12' | '13-16';
  story_type: 'echo_reader';
  sentence_count?: number;
  target_words?: string[];
  custom_prompt?: string;
}

export interface EchoReaderStoriesState {
  stories: EchoReaderStory[];
  isLoading: boolean;
  isGenerating: boolean;
  error: string | null;
}

export function useEchoReaderStories() {
  const { user } = useAuthStore();
  const [state, setState] = useState<EchoReaderStoriesState>({
    stories: [],
    isLoading: false,
    isGenerating: false,
    error: null,
  });

  // Fetch Echo Reader stories
  const fetchStories = useCallback(async (limit: number = 10, offset: number = 0) => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const response = await fetch(`/api/stories?type=echo_reader&limit=${limit}&offset=${offset}`, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      const stories: EchoReaderStory[] = data.data.stories;

      setState(prev => ({
        ...prev,
        stories,
        isLoading: false,
        error: null,
      }));
    } catch (error) {
      console.error('Failed to fetch Echo Reader stories:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to fetch stories',
      }));
    }
  }, []);

  // Generate a new Echo Reader story
  const generateStory = useCallback(async (request: GenerateStoryRequest): Promise<EchoReaderStory | null> => {
    if (!user) {
      setState(prev => ({ ...prev, error: 'User not authenticated' }));
      return null;
    }

    setState(prev => ({ ...prev, isGenerating: true, error: null }));

    try {
      const response = await fetch('/api/stories/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      const data = await response.json();
      const newStory: EchoReaderStory = data.data.story;

      // Add the new story to the beginning of the list
      setState(prev => ({
        ...prev,
        stories: [newStory, ...prev.stories],
        isGenerating: false,
        error: null,
      }));

      return newStory;
    } catch (error) {
      console.error('Failed to generate story:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to generate story';
      
      setState(prev => ({
        ...prev,
        isGenerating: false,
        error: errorMessage,
      }));

      return null;
    }
  }, [user]);

  // Get a specific story by ID
  const getStoryById = useCallback(async (storyId: string): Promise<EchoReaderStory | null> => {
    try {
      const response = await fetch(`/api/stories/${storyId}`, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      return data.data as EchoReaderStory;
    } catch (error) {
      console.error('Failed to fetch story:', error);
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to fetch story',
      }));
      return null;
    }
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  // Get stories filtered by difficulty
  const getStoriesByDifficulty = useCallback((difficulty: string): EchoReaderStory[] => {
    return state.stories.filter(story => story.difficulty === difficulty);
  }, [state.stories]);

  // Get stories filtered by theme
  const getStoriesByTheme = useCallback((theme: string): EchoReaderStory[] => {
    return state.stories.filter(story => story.theme === theme);
  }, [state.stories]);

  // Load initial stories on mount
  useEffect(() => {
    fetchStories();
  }, [fetchStories]);

  return {
    // State
    ...state,
    
    // Actions
    fetchStories,
    generateStory,
    getStoryById,
    clearError,
    
    // Filters
    getStoriesByDifficulty,
    getStoriesByTheme,
  };
}